<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed } from "vue";

import { SignDocumentStatus, type SignDocument } from "@/Modules/SignDocuments/Models/Domain";

import DocumentIcon from "@/Components/Icons/Document.vue";
import TcButton from "@/Components/UI/TcButton.vue";
import TcTag from "@/Components/UI/TcTag.vue";

import { hasValue } from "@/Utils/Objects";
import { unixSecondsToDateTime } from "@/Utils/Date";

export interface DocumentCardProps {
  document: SignDocument;
  isLoading?: boolean;
  isDisabled?: boolean;
  canSelect?: boolean;
}

const props = withDefaults(defineProps<DocumentCardProps>(), {
  canSelect: false,
  isDisabled: false,
  isLoading: false,
});

const emit = defineEmits<{
  (e: "view", document: SignDocument): void;
}>();

const { t } = useI18n();

const isPending = computed(() => props.document.status === SignDocumentStatus.PENDING);
const isRejected = computed(() => props.document.status === SignDocumentStatus.REJECTED);
const isSigned = computed(() => props.document.status === SignDocumentStatus.SIGNED);

const currentVersion = computed(() => props.document.documentVersion);

const date = computed(() => {
  if (hasValue(props.document.signAt)) {
    return unixSecondsToDateTime(props.document.signAt).toFormat("EEE dd MMM yyyy");
  } else {
    return unixSecondsToDateTime(props.document.createdAt).toFormat("EEE dd MMM yyyy");
  }
});
</script>

<template>
  <div class="card">
    <div class="card__header">
      <DocumentIcon size="xl" />
    </div>

    <div class="card__content">
      <div class="card__content__status">
        <TcTag v-if="isPending" size="small" background="secondary-low" color="content-pre-mid">
          {{ t("signs.card.status.pending") }}
        </TcTag>

        <TcTag v-else-if="isRejected" size="small" background="semantic-error" color="semantic-white">
          {{ t("signs.card.status.rejected") }}
        </TcTag>

        <TcTag v-else-if="isSigned" size="small" background="semantic-success" color="semantic-white">
          {{ t("signs.card.status.signed") }}
        </TcTag>
      </div>

      <p class="card__content__title">
        {{ currentVersion.title }}
      </p>

      <p class="card__content__date">
        {{ date }}
      </p>
    </div>

    <div class="card__actions">
      <TcButton variant="outlined" isSmall :disabled="props.isDisabled" @click="emit('view', props.document)">
        {{ t("signs.card.actions.view") }}
      </TcButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card {
  @include colors.bg(content, lowest);
  @include colors.border(content, mid, true);
  @include radius(m);
  @include border(2, solid);
  @include sp.padding(y, 4);
  @include sp.padding(x, 5);

  & {
    display: flex;
    flex-direction: column;
    gap: $space-3;
  }

  @media (max-width: $breakpoint-m) {
    @include sp.padding(y, 2);
    @include sp.padding(x, 3);
  }

  &__header {
    @include sp.padding(y, 5);
    @include colors.bg(background, base);
    @include radius(m);

    display: flex;
    justify-content: center;
  }

  &__content {
    display: grid;
    flex-grow: 2;
    grid-template-areas:
      "status options"
      "title title"
      "date date";
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: $space-3;
    align-items: center;

    &__title {
      @include text.general(high, m);

      grid-area: title;
    }

    &__status {
      grid-area: status;
    }

    &__options {
      grid-area: options;

      &:hover {
        cursor: pointer;
      }
    }

    &__date {
      @include text.general(low, xs);
      @include colors.color(content, high);

      grid-area: date;
    }
  }
}
</style>
